import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  IBankPaymentCode,
  ICollectorAccountCodes,
} from '../../../../common/interfaces/bankPaymentCode.interface'
import { customSwal } from '../../../../config/swal'
import { BankPaymentCodeService } from '../../../../services/BankPaymentCodeService'
import { notifyApiError } from '../../../movements/manual-operations/utils/notifyApiError'

const BANK_PAYMENT_CODE_QUERY_KEY = ['bank-payment-code']

const confirm = async (title: string, confirmButtonText: string) => {
  const result = await customSwal.fire({
    title,
    icon: 'warning',
    showCancelButton: true,
    reverseButtons: true,
    confirmButtonText,
    cancelButtonText: 'Cancelar',
  })
  return result.isConfirmed
}

const notifyError = (error: unknown) =>
  notifyApiError((error as AxiosError<{ message?: string }>)?.response?.data?.message)

export const useBankPaymentCode = () => {
  const queryClient = useQueryClient()
  const service = BankPaymentCodeService()

  const {
    data: banks = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<IBankPaymentCode[]>(BANK_PAYMENT_CODE_QUERY_KEY, service.list)

  const mutationOptions = {
    onSuccess: () => queryClient.invalidateQueries(BANK_PAYMENT_CODE_QUERY_KEY),
    onError: notifyError,
  }

  const toggleMutation = useMutation(
    ({ bankId, enabled }: { bankId: number; enabled: boolean }) => service.toggle(bankId, enabled),
    mutationOptions
  )

  const bankCodeMutation = useMutation(
    ({ bankId, code }: { bankId: number; code: string }) =>
      service.updateBankHabitatCode(bankId, code),
    mutationOptions
  )

  const accountCodesMutation = useMutation(
    ({ accountId, codes }: { accountId: number; codes: ICollectorAccountCodes }) =>
      service.updateCollectorAccountCodes(accountId, codes),
    mutationOptions
  )

  const toggle = async (bank: IBankPaymentCode, enabled: boolean) => {
    const action = enabled ? 'habilitar' : 'deshabilitar'
    if (
      !(await confirm(
        `¿Desea ${action} el código de pago de ${bank.description}?`,
        `Sí, ${action}`
      ))
    )
      return
    toggleMutation.mutate({ bankId: bank.id, enabled })
  }

  const runConfirmed = async (
    title: string,
    mutation: () => Promise<unknown>
  ): Promise<boolean> => {
    if (!(await confirm(title, 'Sí, guardar'))) return false
    return mutation()
      .then(() => true)
      .catch(() => false)
  }

  const saveBankHabitatCode = (bank: IBankPaymentCode, code: string) =>
    runConfirmed(`¿Desea guardar el código Habitat de ${bank.description}?`, () =>
      bankCodeMutation.mutateAsync({ bankId: bank.id, code })
    )

  const saveAccountCodes = (accountId: number, fundTitle: string, codes: ICollectorAccountCodes) =>
    runConfirmed(`¿Desea guardar el código Habitat de ${fundTitle}?`, () =>
      accountCodesMutation.mutateAsync({ accountId, codes })
    )

  return {
    banks,
    isLoading,
    isError,
    refetch,
    togglingBankId: toggleMutation.isLoading ? toggleMutation.variables?.bankId : undefined,
    savingBankId: bankCodeMutation.isLoading ? bankCodeMutation.variables?.bankId : undefined,
    savingAccountId: accountCodesMutation.isLoading
      ? accountCodesMutation.variables?.accountId
      : undefined,
    toggle,
    saveBankHabitatCode,
    saveAccountCodes,
  }
}
